import { windowSizes } from '@/utils/constant';
import { WinDataType, WindowData, WindowState } from '@/utils/types/window';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: WindowState = {
  windows: [],
  focusedWindowId: null,
  nextSequenceIndex: 1,
};

const windowSlice = createSlice({
  initialState,
  name: 'windows',
  reducers: {
    // Action to open a new window
    openWindow: (
      state,
      action: PayloadAction<{
        title: string;
        componentKey: string;
        id: number | null;
        winData?: WinDataType;
        size?: { width?: number; height?: number };
        isAdjustable?: boolean;
        isDraggable?: boolean;
        disableClose?: boolean;
        disableMessage?: string[];
        disableOtherInteractions?: boolean;
        allowMultiple?: boolean;
      }>,
    ) => {
      // If an id is provided, find that specific window
      // Otherwise, find by componentKey (unless allowMultiple is true)
      let existingWindow = null;
      if (action.payload.id) {
        existingWindow = state.windows.find((window: WindowData) => window.id === action.payload.id);
      } else if (!action.payload.allowMultiple) {
        existingWindow = state.windows.find(
          (window: WindowData) => window.componentKey === action.payload.componentKey,
        );
      }

      if (existingWindow) {
        // Reopen the hidden window
        if (!existingWindow.isWindowOpen) {
          existingWindow.isWindowOpen = true;
        }
        existingWindow.winData = action?.payload?.winData;
        existingWindow.title = action.payload.title;
        // When reopening a window, give it a new sequence index to bring it to top
        existingWindow.sequenceIndex = state.nextSequenceIndex++;
        // Set focus to the reopened window
        state.focusedWindowId = existingWindow.id;
      } else {
        // Create a new window.
        // Date.now() alone collides when several windows are opened in the same
        // synchronous tick (e.g. looping over multiple created repair orders),
        // producing duplicate ids -> duplicate React keys -> only some windows render.
        // Bump the id on any collision so rapid creates always get a unique id.
        let uniqueId = Date.now();
        while (state.windows.some((w: WindowData) => w.id === uniqueId)) {
          uniqueId++;
        }

        const newWindow: WindowData = {
          id: uniqueId,
          disableOtherInteractions:
            typeof action.payload.disableOtherInteractions === 'boolean'
              ? (action.payload.disableOtherInteractions ?? false)
              : (() => {
                  const disableOtherInteractions = windowSizes[action.payload.componentKey]?.disableOtherInteractions;
                  return typeof disableOtherInteractions === 'boolean' ? disableOtherInteractions : false;
                })(),

          windowId: `${action.payload.title.toLowerCase().replace(/\s+/g, '_')}_${uniqueId}`,
          title: action.payload.title,
          winData: action?.payload?.winData,
          componentKey: action.payload.componentKey,
          position: {
            x: 0,
            y: 0,
          },
          size: {
            width: 840,
            height: 0,
            ...(action.payload.size ?? windowSizes[action.payload.componentKey]?.size ?? {}),
          },
          isMinimized: false,
          isMaximized: false,
          isWindowOpen: true,
          isAdjustable:
            typeof action.payload.isAdjustable === 'boolean'
              ? action.payload.isAdjustable
              : (() => {
                  const isAdjustable = windowSizes[action.payload.componentKey]?.isAdjustable;
                  return typeof isAdjustable === 'boolean' ? isAdjustable : true;
                })(),
          isDraggable: action.payload.isDraggable ?? true,
          disableClose: action.payload.disableClose ?? false,
          disableMessage: action.payload.disableMessage ?? [],
          sequenceIndex: state.nextSequenceIndex++,
        };
        state.windows.push(newWindow);
        // Set focus to the new window
        state.focusedWindowId = newWindow.id;
      }
    },

    // Action to close a window
    closeWindow: (state, action: PayloadAction<number>) => {
      state.windows = state.windows.filter((w: WindowData) => w.id !== action.payload);
      if (state.focusedWindowId === action.payload) {
        // Find the window with the highest sequenceIndex that is open and not minimized
        const openWindows = state.windows
          .filter((w) => w.isWindowOpen && !w.isMinimized)
          .sort((a, b) => (b.sequenceIndex || 0) - (a.sequenceIndex || 0));

        // Focus the top-most window if available
        state.focusedWindowId = openWindows.length > 0 ? openWindows[0].id : null;
      }

      // Reset sequence index when no windows are left
      if (state.windows.length === 0) {
        state.nextSequenceIndex = 1;
        state.focusedWindowId = null;
      }
    },
    // Action to minimize a window
    minimizeWindow: (state, action: PayloadAction<number>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload);
      if (window) {
        window.isMinimized = true;
        window.isMaximized = false;
      }
    },
    // Action to maximize a window
    maximizeWindow: (state, action: PayloadAction<number>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload);
      if (window) {
        window.isMaximized = true;
        window.isMinimized = false;
      }
    },
    // Action to hide a window
    hideWindow: (state, action: PayloadAction<number>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload);
      if (window) {
        window.isWindowOpen = false;

        // If this was the focused window, focus the next topmost window
        if (state.focusedWindowId === action.payload) {
          const openWindows = state.windows
            .filter((w) => w.isWindowOpen && !w.isMinimized)
            .sort((a, b) => (b.sequenceIndex || 0) - (a.sequenceIndex || 0));

          state.focusedWindowId = openWindows.length > 0 ? openWindows[0].id : null;
        }
      }
    },
    // Action to focus a window
    focusWindow: (state, action: PayloadAction<number>) => {
      if (state.focusedWindowId === action.payload) {
        return;
      }

      const windowToFocus = state.windows.find((w) => w.id === action.payload);
      if (!windowToFocus) return;

      // Give the focused window the highest sequence index
      windowToFocus.sequenceIndex = state.nextSequenceIndex++;

      state.focusedWindowId = action.payload;
    },
    // Action to update window position
    updateWindowPosition: (state, action: PayloadAction<{ id: number; position: { x: number; y: number } }>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload.id);
      if (window) {
        window.position = action.payload.position;
      }
    },
    // Action to update window size
    updateWindowSize: (state, action: PayloadAction<{ id: number; size: { width?: number; height?: number } }>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload.id);
      if (window) {
        window.size = { ...window.size, ...action.payload.size };
      }
    },
    // Action to update window title
    updateWindowTitle: (state, action: PayloadAction<{ id: number; title: string }>) => {
      const window = state.windows.find((w: WindowData) => w.id === action.payload.id);
      if (window) {
        window.title = action.payload.title;
      }
    },
    switchToNextWindow: (state) => {
      const openWindows = state.windows.filter((window) => window.isWindowOpen && !window.isMinimized);
      if (openWindows.length <= 1) return;
      openWindows.sort((a, b) => a.id - b.id);
      const currentFocusedIndex = openWindows.findIndex((window) => window.id === state.focusedWindowId);

      const nextIndex = currentFocusedIndex >= 0 ? (currentFocusedIndex + 1) % openWindows.length : 0;
      const nextWindow = openWindows[nextIndex];
      if (nextWindow) {
        // Give the focused window the highest sequence index
        nextWindow.sequenceIndex = state.nextSequenceIndex++;
        state.focusedWindowId = nextWindow.id;
      }
    },
    switchToPreviousWindow: (state) => {
      const openWindows = state.windows.filter((window) => window.isWindowOpen && !window.isMinimized);

      if (openWindows.length <= 1) return;

      openWindows.sort((a, b) => a.id - b.id);

      const currentFocusedIndex = openWindows.findIndex((window) => window.id === state.focusedWindowId);

      const getPreviousIndex = (currentIndex: number, total: number): number => {
        if (currentIndex >= 0) {
          return currentIndex === 0 ? total - 1 : currentIndex - 1;
        }
        return total - 1;
      };

      const prevIndex = getPreviousIndex(currentFocusedIndex, openWindows.length);

      const prevWindow = openWindows[prevIndex];
      if (prevWindow) {
        // Give the focused window the highest sequence index
        prevWindow.sequenceIndex = state.nextSequenceIndex++;
        state.focusedWindowId = prevWindow.id;
      }
    },
    updateWindowWinData: (state, action: PayloadAction<{ id: number; winData: Partial<WinDataType> }>) => {
      const window = state.windows.find((win) => win.id === action.payload.id);
      if (!window) return;

      const cleanUndefined = (obj: unknown): unknown => {
        // Return non-objects and arrays as-is
        if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
          return obj;
        }

        // Only process plain objects
        if ((obj as Record<string, unknown>).constructor !== Object) {
          return obj;
        }

        const result: Record<string, unknown> = {};
        const objRecord = obj as Record<string, unknown>;

        for (const key in objRecord) {
          if (Object.hasOwn(objRecord, key) && objRecord[key] !== undefined) {
            result[key] = cleanUndefined(objRecord[key]);
          }
        }

        return result;
      };

      const cleaned = cleanUndefined(action.payload.winData) as Partial<WinDataType>;
      // If cleaned object is empty → reset winData entirely
      if (Object.keys(cleaned).length === 0) {
        window.winData = {};
        return;
      }
      // Otherwise merge with existing winData
      window.winData = { ...(window.winData ?? {}), ...cleaned } as WinDataType;
    },
  },
});

export const {
  openWindow,
  closeWindow,
  minimizeWindow,
  maximizeWindow,
  hideWindow,
  focusWindow,
  updateWindowPosition,
  updateWindowSize,
  updateWindowWinData,
  updateWindowTitle,
  switchToNextWindow,
  switchToPreviousWindow,
} = windowSlice.actions;

export default windowSlice.reducer;
